import { View, StyleSheet, TouchableOpacity, FlatList, Text, Modal, TextInput } from 'react-native';
import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useNavigation, useIsFocused } from '@react-navigation/native';
import { MaterialIcons } from '@expo/vector-icons';
import useDataStore from '../stores/dataStore';
import { makeApiRequest } from '../utils/apiUtil';
import Search from '../components/Search';
import Loader from '../components/Loader';
import { handleAlertForError } from '../utils/handleError';
import List from '../components/List';
import Button from '../components/Button';
import { APP_ROLE, EVENT_ACTIONS } from '../constants/Constant';
import { Colors } from '../constants/theme';
import MixpanelTracker from '../utils/MixpanelTracker';

const OtherIncomeScreen = () => {
    const navigation = useNavigation();
    const [otherIncomeAccountKey, setOtherIncomeAccountKey] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isPopupVisible, setPopupVisible] = useState(false);
    const [otherIncomeAccount, setOtherIncomeAccount] = useState('');
    const [errorModal, setErrorModal] = useState(null);
    const [API_CONFIG, userData, authToken, setAuthToken, refreshToken, setRefreshToken] = useDataStore((state) => ([state.API_CONFIG, state.userData, state.authToken, state.setAuthToken, state.refreshToken, state.setRefreshToken]));
    const [searchQuery, setSearchQuery] = useState('');
    const isFocused = useIsFocused();

    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => (
                <TouchableOpacity style={styles.headerRight} onPress={() => handleIconPress()}>
                    <MaterialIcons name="history" size={24} color="black" />
                </TouchableOpacity>
            ),
            title: "Income Accounts"
        });
    });

    useEffect(() => {
        if (isFocused) {
            MixpanelTracker.sendAnalyticsEvent(EVENT_ACTIONS.OTHER_INCOME_CLICKED, userData);
            getAllAccounts();
        }
    }, [isFocused]);

    const getAllAccounts = async () => {
        try {
            const accountResponse = await makeApiRequest('get', API_CONFIG.OTHER_INCOME, authToken, refreshToken, setAuthToken, setRefreshToken);
            const dataArray = Object.entries(accountResponse).map(([key, value]) => ({
                id: key,
                name: value
            }));
            setOtherIncomeAccountKey(dataArray);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            handleAlertForError(error);
        }
    }

    const handleIconPress = () => {
        navigation.navigate('OtherIncome');
    };

    const handleAddAccount = () => {
        MixpanelTracker.sendAnalyticsEvent(EVENT_ACTIONS.OTHER_INCOME_ADD_ACCOUNT, userData);
        setErrorModal('');
        setPopupVisible(true);
    };

    const handleSubmit = () => {
        let isErr = false;
        for (let i = 0; i < otherIncomeAccountKey?.length; i++) {
            if (otherIncomeAccount.toLowerCase().trim() === otherIncomeAccountKey[i]?.name.toLowerCase()) {
                isErr = true;
            }
        }
        if (isErr) setErrorModal("Income Account Already Exist")
        else {
            if (!otherIncomeAccount) {
                setErrorModal('Please enter income name.');
                return false;
            }
            else {
                addAcountToDB();
                MixpanelTracker.sendAnalyticsEvent(EVENT_ACTIONS.OTHER_INCOME_ADD_ACCOUNT, userData);
                setPopupVisible(false);
            }
            setErrorModal('');
        }
    };

    const addAcountToDB = async () => {
        try {
            if (otherIncomeAccount) {
                await makeApiRequest("post", `${API_CONFIG.OTHER_INCOME}/${otherIncomeAccount.trim()}`, authToken, refreshToken, setAuthToken, setRefreshToken)
                getAllAccounts();
                setOtherIncomeAccount('');
            } else {
                setErrorModal('Enter other income name');
            }
        } catch (error) {
            handleAlertForError(error);
        }
    }

    const filteredAccounts = otherIncomeAccountKey?.filter(item => {
        return item.name.toLowerCase().includes(searchQuery.toLowerCase());
    });

    return (
        <View style={styles.container}>
            <Search value={searchQuery} onChangeText={setSearchQuery} placeholder="Search Income Account" />
            {loading ? (
                <View style={styles.loaderContainer}>
                    <Loader size="large" />
                </View>
            ) : filteredAccounts?.length > 0 ? (
                <FlatList
                    data={filteredAccounts}
                    renderItem={({ item }) => (
                        <List
                            name={item.name}
                            type={'IncomeAccount'}
                            accountId={item.id}
                        />
                    )}
                    style={{ height: '100%', marginBottom: 85 }}
                    keyExtractor={item => item.id}
                />
            ) : (
                <View style={styles.noAccContainer}>
                    {searchQuery ? <Text>No Income Accounts Found</Text> : <Text style={styles.noAccText}> No Income Accounts Added</Text>}
                </View>
            )}
            <Modal
                animationType="slide"
                transparent={true}
                visible={isPopupVisible}
                onRequestClose={() => setPopupVisible(false)}>
                <View style={styles.centeredView}>
                    <View style={styles.modalView}>
                        <TextInput
                            style={styles.textInput}
                            onChangeText={(text) => setOtherIncomeAccount(text)}
                            placeholder="Income Account"
                            value={otherIncomeAccount} />
                        {errorModal && <Text style={styles.errorText}>{errorModal}</Text>}
                        <Button
                            onPress={handleSubmit}
                            buttonStyle={styles.submitButton}
                            btnLabel={"Submit"} />
                    </View>
                </View>
            </Modal>
            <Button
                onPress={handleAddAccount}
                btnLabel={"ADD INCOME ACCOUNT"}
                buttonStyle={styles.btnContainer}
                allowedRole={[APP_ROLE.OWNER]}
            />
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    headerRight: {
        paddingRight: 20,
        paddingTop: 8,
    },
    loaderContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    noAccContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    noAccText: {
        textAlign: 'center',
        fontSize: 15,
    },
    btnContainer: {
        backgroundColor: Colors.secondary,
        paddingVertical: 20,
        paddingHorizontal: 40,
        width: '90%',
        position: 'absolute',
        bottom: 0,
        margin: 20,
    },
    errorText: {
        color: 'red',
        marginBottom: 10,
    },
    textInput: {
        borderWidth: 2,
        borderColor: '#636363',
        margin: 10,
        padding: 12,
        fontSize: 16,
        borderRadius: 6,
        width: '90%'
    },
    centeredView: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0,0,0,0.5)',
    },
    modalView: {
        backgroundColor: 'white',
        borderRadius: 10,
        padding: 20,
        width: '80%',
        alignItems: 'center',
    },
    submitButton: {
        backgroundColor: Colors.primary,
        width: '90%',
    },
});

export default OtherIncomeScreen;